import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Plus, FileText, Wrench, GitBranch, Globe, FolderOpen, Trash2, X,
} from 'lucide-react';
import { useSkills, useCreateSkill, useDeleteSkill } from '../hooks/useSkills';
import { SkillType, SkillScope, type Skill, type CreateSkillDto } from '../types/skill';

const TYPE_CONFIG: Record<string, { icon: typeof FileText; label: string; color: string }> = {
  [SkillType.PROMPT_TEMPLATE]: { icon: FileText, label: 'Prompt Template', color: 'text-blue-600 bg-blue-50' },
  [SkillType.TOOL_CONFIG]: { icon: Wrench, label: 'Tool Config', color: 'text-purple-600 bg-purple-50' },
  [SkillType.WORKFLOW]: { icon: GitBranch, label: 'Workflow', color: 'text-green-600 bg-green-50' },
};

const SCOPE_CONFIG: Record<string, { icon: typeof Globe; label: string }> = {
  [SkillScope.GLOBAL]: { icon: Globe, label: 'Global' },
  [SkillScope.PROJECT]: { icon: FolderOpen, label: 'Project' },
};

function SkillCard({ skill, onDelete }: { skill: Skill; onDelete: (id: string) => void }) {
  const typeCfg = TYPE_CONFIG[skill.type] || TYPE_CONFIG[SkillType.PROMPT_TEMPLATE];
  const scopeCfg = SCOPE_CONFIG[skill.scope] || SCOPE_CONFIG[SkillScope.PROJECT];
  const TypeIcon = typeCfg.icon;
  const ScopeIcon = scopeCfg.icon;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`rounded-lg p-2 ${typeCfg.color}`}>
            <TypeIcon className="h-4 w-4" />
          </div>
          <div>
            <Link
              to={`/skills/${skill.id}`}
              className="text-sm font-medium text-slate-900 hover:text-blue-600"
            >
              {skill.name}
            </Link>
            {skill.description && (
              <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">{skill.description}</p>
            )}
            <div className="mt-2 flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${typeCfg.color}`}>
                {typeCfg.label}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                <ScopeIcon className="h-3 w-3" />
                {scopeCfg.label}
              </span>
            </div>
            {skill.variables && skill.variables.length > 0 && (
              <p className="mt-1 text-xs text-slate-400">
                {skill.variables.length} variable{skill.variables.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={() => onDelete(skill.id)}
          className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function Skills() {
  const { id: projectId } = useParams<{ id: string }>();
  const { data: skills, isLoading } = useSkills(projectId);
  const createSkill = useCreateSkill(projectId);
  const deleteSkill = useDeleteSkill();
  const [showForm, setShowForm] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');

  const [form, setForm] = useState<CreateSkillDto>({
    name: '',
    type: SkillType.PROMPT_TEMPLATE,
    scope: projectId ? SkillScope.PROJECT : SkillScope.GLOBAL,
  });

  const filtered = skills?.filter(s => filterType === 'all' || s.type === filterType) ?? [];

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    await createSkill.mutateAsync(form);
    setForm({ name: '', type: SkillType.PROMPT_TEMPLATE, scope: projectId ? SkillScope.PROJECT : SkillScope.GLOBAL });
    setShowForm(false);
  };

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Skills</h1>
          <p className="text-sm text-slate-500">Reusable prompt templates, tool configs, and workflows</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          New Skill
        </button>
      </div>

      {/* Filter bar */}
      <div className="mb-4 flex gap-2">
        {[
          { value: 'all', label: 'All' },
          { value: SkillType.PROMPT_TEMPLATE, label: 'Templates' },
          { value: SkillType.TOOL_CONFIG, label: 'Tools' },
          { value: SkillType.WORKFLOW, label: 'Workflows' },
        ].map(f => (
          <button
            key={f.value}
            onClick={() => setFilterType(f.value)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              filterType === f.value
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Create form */}
      {showForm && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-medium text-slate-900">New Skill</h3>
            <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input
              type="text"
              placeholder="Skill name"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none"
            />
            <select
              value={form.type}
              onChange={e => setForm({ ...form, type: e.target.value as SkillType })}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none"
            >
              <option value={SkillType.PROMPT_TEMPLATE}>Prompt Template</option>
              <option value={SkillType.TOOL_CONFIG}>Tool Config</option>
              <option value={SkillType.WORKFLOW}>Workflow</option>
            </select>
            <select
              value={form.scope}
              onChange={e => setForm({ ...form, scope: e.target.value as SkillScope })}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none"
            >
              <option value={SkillScope.GLOBAL}>Global</option>
              <option value={SkillScope.PROJECT}>Project</option>
            </select>
            <textarea
              placeholder="Description (optional)"
              value={form.description || ''}
              onChange={e => setForm({ ...form, description: e.target.value || undefined })}
              rows={2}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none sm:col-span-2"
            />
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={handleCreate}
              disabled={!form.name.trim() || createSkill.isPending}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {createSkill.isPending ? 'Creating...' : 'Create Skill'}
            </button>
          </div>
        </div>
      )}

      {/* Skills grid */}
      {isLoading ? (
        <div className="py-12 text-center text-sm text-slate-400">Loading skills...</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center">
          <FileText className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">No skills yet</p>
          <p className="text-xs text-slate-400">Create your first skill to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(skill => (
            <SkillCard key={skill.id} skill={skill} onDelete={id => deleteSkill.mutate(id)} />
          ))}
        </div>
      )}
    </div>
  );
}
